import { findWhere } from "underscore";
import { Alert } from "reactstrap";
import pluralize from "pluralize";

import useUpdateSubitemTableLayout from "../api/api-hooks/subitems/useUpdateSubitemTableLayout";
import useUpdateSubitemField from "../api/api-hooks/subitems/useUpdateSubitemField";
import AppTable from "../components/AppTable/AppTable";

const EditableTableView = ({
  moduleAPIName,
  query,
  setQuery,
  records,
  fieldSchema,
  parentRecord,
  selected = null, // The selected rows, provide if you want selectableRows
  setSelected = null, // The state setter for selected rows
  emptyMessage,
  foundLayout,
  selectedLayout,
  providedLayouts,
  containerClasses = "",
  columnAdd = true,
  loading = false,
  onSort,
  createMode = false,
  onChangeCreate,
  setDraggingID,
  draggingID,
  reusableViewReferenceFields,
}) => {
  const { updateSubitemField, updatingSubitem } = useUpdateSubitemField();
  const { updateSubitemTableLayout, updatingSubitemTableLayout } =
    useUpdateSubitemTableLayout();

  let currentLayouts;

  if (Array.isArray(providedLayouts)) {
    currentLayouts = providedLayouts;
  } else {
    currentLayouts =
      fieldSchema?.TableLayouts?.length > 0 ? fieldSchema?.TableLayouts : [];
  }

  const onColumnReorder = (query) => {
    let newQuery = [
      {
        QueryField: fieldSchema.APIName,
        Query: {
          ...query,
        },
      },
    ];

    setQuery(newQuery);

    if (createMode || !foundLayout) return;

    let layout = findWhere(currentLayouts, { id: selectedLayout });

    if (!layout) return;

    layout.Fields = query?.Fields;

    let updatedLayouts = currentLayouts.map((l) => {
      if (l.id === selectedLayout) {
        return layout;
      }

      return l;
    });

    const updatedLayoutIsSelected =
      newQuery?.[0]?.Query?._SelectedLayout === layout.id;

    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        ...(updatedLayoutIsSelected && { Query: newQuery }),
        TableLayouts: updatedLayouts,
      },
    });
  };

  const updateQueryFields = (field, type) => {
    let newQuery;

    if (type === "Add") {
      newQuery = [
        {
          QueryField: fieldSchema.APIName,
          Query: {
            ...query,
            Fields: [...query.Fields, field.APIName],
          },
        },
      ];
    } else if (type === "Remove") {
      newQuery = [
        {
          QueryField: fieldSchema.APIName,
          Query: {
            ...query,
            Fields: query.Fields?.filter((item) => item !== field),
          },
        },
      ];
    }

    setQuery(newQuery);
    if (createMode) return;

    if (!foundLayout) return;

    let updatedLayout = findWhere(currentLayouts, { id: selectedLayout });
    let updatedLayouts;

    if (type === "Add") {
      updatedLayout.Fields.push(field.APIName);
    } else if (type === "Remove") {
      updatedLayout.Fields = updatedLayout.Fields.filter(
        (item) => item !== field
      );
    }

    updatedLayouts = currentLayouts.map((layout) => {
      if (layout.id === selectedLayout) {
        return updatedLayout;
      }
      return layout;
    });

    // Check if updatedLayout is the selectedLayout
    if (newQuery?.[0]?.Query?._SelectedLayout === updatedLayout.id) {
      updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          Query: newQuery,
          TableLayouts: updatedLayouts,
        },
      });
      return;
    }

    updateSubitemTableLayout({
      moduleAPIName: parentRecord._ModuleAPIName,
      fieldAPIName: fieldSchema.APIName,
      payload: {
        TableLayouts: updatedLayouts,
      },
    });
  };

  const handleChange = (values, recordID, key) => {
    const value = values[key];
    onChangeCreate(key, value, recordID);
  };

  return (
    <div className="table-responsive">
      {records && records?.length > 0 ? (
        <AppTable
          moduleAPIName={moduleAPIName}
          query={query}
          selectableRows={true}
          columnAdd={columnAdd}
          columnAddMessage={`Adding Field to ${
            foundLayout?.Name ?? "Default Layout"
          }`}
          onColumnAdd={(field) => updateQueryFields(field, "Add")}
          onColumnRemove={(field) => updateQueryFields(field, "Remove")}
          onColumnReorder={onColumnReorder}
          selectedRecords={selected}
          setSelectedRecords={setSelected}
          data={records}
          sticky={true}
          containerClasses={containerClasses ?? "pr-4"}
          multiSubitemFieldAPIName={fieldSchema?.APIName}
          parentRecord={parentRecord}
          loading={loading}
          onSort={onSort}
          createMode={createMode}
          onChangeCreate={handleChange}
          setDraggingID={setDraggingID}
          draggingID={draggingID}
          reusableViews={{ ReferenceFields: reusableViewReferenceFields }}
        />
      ) : (
        <Alert
          color="superlight"
          className="font-weight-500 font-weight-500 m-2"
        >
          No {pluralize(fieldSchema?.Name ?? "Record")}{" "}
          {emptyMessage ? emptyMessage : "have been added."}
        </Alert>
      )}
    </div>
  );
};

export default EditableTableView;
